import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { RollingNumber } from '@/components/ui/rolling-number';
import { cn } from '@/lib/utils';

export interface StatCardProps {
  label: string;
  /** A number is smoothly counted up; a string is shown as-is (e.g. "94%"). */
  value: string | number;
  icon: LucideIcon;
  /** Small clarifying line directly under the label, e.g. "(already existed)". */
  sublabel?: ReactNode;
  /** Percentage change vs. the previous period. Positive = up, negative = down. */
  delta?: number;
  /** Small context line under the delta, e.g. "vs. last 30 days". */
  hint?: string;
  className?: string;
}

/**
 * KPI tile used across dashboards. Value + labelled metric, an optional
 * trend delta (green up / red down) and a supporting hint line.
 */
export function StatCard({ label, value, icon: Icon, sublabel, delta, hint, className }: StatCardProps) {
  const hasDelta = typeof delta === 'number';
  const isUp = (delta ?? 0) >= 0;
  const TrendIcon = isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <Card className={cn('transition-shadow duration-200 hover:shadow-md', className)}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium leading-tight text-muted-foreground">{label}</p>
            {sublabel ? (
              <p className="mt-0.5 text-[11px] font-normal text-muted-foreground/70">{sublabel}</p>
            ) : null}
          </div>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:size-9">
            <Icon className="size-4 sm:size-5" aria-hidden />
          </span>
        </div>
        <div className="mt-2 flex items-baseline gap-2 sm:mt-3">
          <span className="text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl">
            {typeof value === 'number' ? <RollingNumber value={value} /> : value}
          </span>
          {hasDelta ? (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 text-xs font-medium',
                isUp ? 'text-emerald-600 dark:text-emerald-500' : 'text-destructive'
              )}
            >
              <TrendIcon className="size-3.5" aria-hidden />
              {Math.abs(delta as number)}%
            </span>
          ) : null}
        </div>
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}
